import i18next from 'i18next';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
  buildAttributesGridColumns,
  formatAttributeValues,
} from '../../services/common/AttributeUtils';
import { filterDossiers } from '../../services/common/ChartUtils';
import { Context } from '../../states/Store';
import EADataGrid from '../common/datatable/EADataGrid';
import DossierDeactivationDialog from './DossierDeactivationDialog';
import {
  getDossiersDataTableColumns,
  dossiersDefaultVisibilityColumns,
} from './DossiersDataTableUtils';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import {
  getExportFileFromAccountDossiers,
  reactivateDossier,
} from '../../services/edge/DossierService';
import { ERROR, Exports, FieldType, SUCCESS } from '../../services/common/Constants';
import ROUTES from '../../config/Routes';
import { GridLoadingOverlay } from '@mui/x-data-grid';
import { useColumnsVisibility } from 'components/common/hooks/useColumnsVisibility';
import { getAccountAttributesByBondType } from 'services/edge/AttributesService';
import { isEmpty } from 'lodash';
import { buildGridColumnsForCalculations, CalculationTargetResourceType, formatCalculationResult } from 'services/common/CalculatorUtils';
import { getAccountCalculations } from 'services/edge/CalculatorService';

export const getDossierTypeLibelle = (dossierTypeLibelle) => {
  let libelle = !dossierTypeLibelle ? i18next.t('free') : dossierTypeLibelle;
  return libelle;
};

function DossiersDataTable({
  dossiers,
  chartFilters,
  monitored = true,
  component = 'dossiers-list',
  pageSize,
  sheetVariant = false,
  onDeactivation,
  onReactivateDossierNavigation,
  multiDataGridPage = false,
  fromTp = false,
  sortModel,
  onSortModelChange = () => {},
  onVisibleRowsCountChange = () => {},
  paginationMode = 'client',
  filters,
  contextThirdparty = null,
  ...other
}) {
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const [mappedDossiers, setMappedDossiers] = useState([]);
  const [shownDossiers, setShownDossiers] = useState();
  const [selectedDossierId, setSelectedDossierId] = useState();
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [dossiersColumns, setDossiersColumns] = useState();
  const [dossiersTypesLabels, setDossiersTypesLabels] = useState();
  const [attributesColumns, setAttributesColumns] = useState();
  const [calculatedAttributesColumns, setCalculatedAttributesColumns] = useState();
  const [indicatorsColumns, setIndicatorsColumns] = useState();
  const { hasAnyOfPrivileges, hasAnyOfPrivilegesOnScopedResourceImpl, hasAnyOfPrivilegesOnPropertyId } = useContext(RoleBasedAccessControlContext);

  const [attributes, setAttributes] = useState();
    const [calculatedAttributes, setCalculatedAttributes] = useState();
    const [indicators, setIndicators] = useState();

  const {
    columnsVisibility,
    setColumnsVisibility,
    setInitialColumnsVisibility,
  } = useColumnsVisibility(component, component === 'dossiers-list');

  useEffect(() => {
    // Map dossiers
    setMappedDossiers(
      dossiers.map((d) => {
        return {
          ...d,
          id: d.dossierId,
          endDate: d.stoppingDate ?? d.expirationDate,
          duns: !d.dunsEncrypted ? d.duns : "",
          vat: d.tva,
          dossierTypeLibelle: getDossierTypeLibelle(d.dossierTypeLibelle),
          ...d.attributeValues?.reduce(
            (res, attr) => ({
              ...res,
              [`attribute-${attr.label}`]: formatAttributeValues(attr),
            }),
            {}
          ),
          ...d.calculatedAttributes?.reduce(
            (res, attr) => ({
              ...res,
              [`calculatedAttribute-${attr.name}`]: formatCalculationResult(attr),
            }),
            {}
          ),
          ...d.indicators?.reduce(
            (res, attr) => ({
              ...res,
              [`indicator-${attr.name}`]: formatCalculationResult(attr),
            }),
            {}
          ),
        };
      })
    );
  }, [dossiers]);

  // Fetch Account Dossiers Attributes
  useEffect(() => {
    if (state.account) {
      (async function fetchData() {
        try {
          const res = await getAccountAttributesByBondType(
            state.account.id,
            'dossier',
            dispatch
          );
          setAttributes(res.content);
        } catch (e) {
          dispatch({ type: 'ALERT', alert: { type: ERROR, msg: e.message } });
          setAttributes([]);
        }
      })();
    }
  }, [state.account, dispatch]);

    // Fetch calculations
    useEffect(() => {
      if (state.account) {
        (async function fetchData() {
          try {
            const res = await getAccountCalculations(state.account.id, CalculationTargetResourceType.DOSSIER, dispatch);
            
            setCalculatedAttributes(
              res.content?.filter(
                (x) => x.nature === FieldType.CALCULATED_ATTRIBUTE
              )
            );
  
            setIndicators(
              res.content?.filter((x) => x.nature === FieldType.INDICATOR)
            );
          } catch (e) {
            dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
            setCalculatedAttributes([]);
            setIndicators([]);
          }
        })();
      }
    }, [state.account, dispatch]);

  useEffect(() => {
    if (attributes && calculatedAttributes && indicators) {
      if (!isEmpty(attributes) && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE)) {
        setAttributesColumns(buildAttributesGridColumns(attributes?.filter(attr => hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE))));
      } else {
        setAttributesColumns([]);
      }
      if (!isEmpty(calculatedAttributes)) {
        setCalculatedAttributesColumns(buildGridColumnsForCalculations(calculatedAttributes?.filter(attr =>// TODO use dedicated Privilege when ready
          hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE))))
      } else {
        setCalculatedAttributesColumns([]);
      }

      if (!isEmpty(indicators)) {
        setIndicatorsColumns(buildGridColumnsForCalculations(indicators.filter(cal => // TODO use dedicated Privilege when ready
          hasAnyOfPrivilegesOnPropertyId(cal.id, PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE_VALUE))))
      } else {
        setIndicatorsColumns([]);
      }
    }
  }, [attributes, calculatedAttributes, indicators, hasAnyOfPrivileges, hasAnyOfPrivilegesOnPropertyId])

  // Filter Dossiers using charts
  useEffect(() => {
    let filteredDossiers = mappedDossiers;
    if (chartFilters) {
      filteredDossiers = filterDossiers(mappedDossiers, chartFilters);
    }
    setShownDossiers(filteredDossiers);
  }, [chartFilters, mappedDossiers]);

  useEffect(() => {
    if (shownDossiers) {
      setDossiersTypesLabels(
        [
          ...new Set(shownDossiers.map((x) => x.dossierTypeLibelle)),
        ].sort((a, b) => a.localeCompare(b))
      );
    }
  }, [shownDossiers]);

  const onStopDossier = useCallback((dossierId) => {
    setSelectedDossierId(dossierId);
    setIsDeactivateDialogOpen(true);
  }, []);

  const onReactivateDossier = useCallback(
    async (dossierId, dossier) => {
      try {
        await reactivateDossier(state.account.id, dossierId, dossier, dispatch);
        if (onReactivateDossierNavigation) {
          onReactivateDossierNavigation();
        } else {
          history.push(
            `/${state.account.id}/${ROUTES.DOSSIER_DETAIL.replace(
              ':dossierId',
              dossierId
            )}`
          );
        }
      } catch (error) {
        dispatch({
          type: 'ALERT',
          alert: { type: ERROR, msg: 'REACTIVATE_DOSSIER_ERROR' },
        });
      }
    },
    [state.account.id, dispatch, history, onReactivateDossierNavigation]
  );

  useEffect(() => {
    if (dossiersTypesLabels && attributesColumns) {
      setDossiersColumns(
        getDossiersDataTableColumns(
          component,
          state.account.id,
          monitored,
          dossiersTypesLabels,
          attributesColumns,
          calculatedAttributesColumns,
          indicatorsColumns,
          onStopDossier,
          onReactivateDossier,
          history,
          hasAnyOfPrivilegesOnScopedResourceImpl,
          state.dossierTypes.map((item) => item.libelle),
          sortModel,
          contextThirdparty
        )
      );
    }
  }, [
    component,
    state.account,
    monitored,
    dossiersTypesLabels,
    attributesColumns,
    calculatedAttributesColumns,
    indicatorsColumns,
    onStopDossier,
    onReactivateDossier,
    history,
    hasAnyOfPrivilegesOnScopedResourceImpl,
    state.dossierTypes,
    sortModel,
    contextThirdparty
  ]);

  useEffect(() => {
    if (attributesColumns) {
      setInitialColumnsVisibility(
        dossiersDefaultVisibilityColumns(
          attributesColumns,
          calculatedAttributesColumns,
          indicatorsColumns, 
          monitored
        )
      );
    }
  }, [attributesColumns, calculatedAttributesColumns, indicatorsColumns, monitored, component, setInitialColumnsVisibility]);

  const handleDeactivationDialogClose = (isDeactivated) => {
    setIsDeactivateDialogOpen(false);
    if (isDeactivated) {
      setShownDossiers(
        shownDossiers.filter((doss) => doss.dossierId !== selectedDossierId)
      );
      onDeactivation && onDeactivation();
    }
  };

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel?.[0] !== sortModel?.[0]) {
      onSortModelChange(newSortModel);
      if (paginationMode === 'server') {
        dispatch({
          type: 'PAGINATION',
          pagination: {
            page: 0,
            rowPerPage: state.pagination.rowPerPage,
          },
        });
      }
    }
  };

  const externalExportHandler = async (exportType) => {
    let objetResult = null;
    dispatch({
      type: 'ALERT',
      alert: {
        type: SUCCESS,
        msg: i18next.t(`tableLabels.toolbar.downloadLoading`),
      },
    });
    const exportDto = {
      columnsVisibility: {
        ...columnsVisibility,
        statusCode: monitored && columnsVisibility.statusCode,
      },
    };
    const onboarded = monitored ? true : null;
    switch (exportType) {
      case Exports.CSV:
      case Exports.XLS:
      case Exports.XLSX:
        objetResult = await getExportFileFromAccountDossiers(
          state.account.id,
          onboarded,
          monitored,
          exportType,
          filters,
          sortModel,
          exportDto,
          dispatch
        );
        break;
      default:
        console.log("Type d'export dossiers non géré");
        break;
    }

    if (objetResult !== null) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([objetResult.arrayBuffer]));
      a.download = objetResult.filename;
      a.click();
    }
  };

  return (
    <>
      {dossiersColumns && columnsVisibility && (
        <EADataGrid
          inputRows={shownDossiers}
          columns={dossiersColumns}
          initialVisibilityModel={columnsVisibility}
          updateVisibilityModel={setColumnsVisibility}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSize={pageSize}
          hasCustomPagination={!multiDataGridPage}
          variant={sheetVariant ? 'sheet' : 'default'}
          onVisibleRowsCountChange={onVisibleRowsCountChange}
          hasFilters={paginationMode === 'client'}
          paginationMode={paginationMode}
          {...(paginationMode === 'server'
            ? {
                externalExportHandler,
                LoadingOverlay: GridLoadingOverlay, // TODO: default loading overlay while fixing the LinearProgress not showing
                rowCount: state.pagination?.count || 0,
              }
            : {})}
          {...other}
        />
      )}
      {isDeactivateDialogOpen &&
        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER) && (
          <DossierDeactivationDialog
            dossierId={selectedDossierId}
            isOpen={isDeactivateDialogOpen}
            fromTp={fromTp}
            handleClose={(isDeactivated) =>
              handleDeactivationDialogClose(isDeactivated)
            }
          />
        )}
    </>
  );
}

export default DossiersDataTable;
